import React, { useState, useEffect } from 'react';
import { FiCreditCard, FiZap, FiCheckCircle, FiLoader, FiList, FiClock, FiSmartphone } from 'react-icons/fi';
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { load } from '@cashfreepayments/cashfree-js';
import axios from 'axios';
import { auth as firebaseAuth } from '../../firebase/config';
import SEO from '../layout/SEO';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Wallet = ({ balance, isGuest, user, onUpdateUser }) => {
    const [paymentMethod, setPaymentMethod] = useState('paypal'); // 'paypal' or 'upi' (now Cashfree)
    const [selectedAmount, setSelectedAmount] = useState(5);      // Default to $5 (STARTER)
    const [selectedINRAmount, setSelectedINRAmount] = useState(99); // Default to ₹99 (STARTER)
    const [status, setStatus] = useState('idle'); // idle, processing, success, error
    const [transactions, setTransactions] = useState([]);
    const [{ isPending }] = usePayPalScriptReducer();

    // Cashfree Instance
    const [cashfree, setCashfree] = useState(null);

    // Initialize Cashfree
    useEffect(() => {
        const initCashfree = async () => {
            try {
                const cf = await load({
                    mode: process.env.REACT_APP_CASHFREE_MODE || "sandbox"
                });
                setCashfree(cf);
            } catch (error) {
                console.error("Cashfree SDK failed to load:", error);
            }
        };
        initCashfree();
    }, []);

    // Helper to get headers for request
    const getHeaders = async () => {
        const headers = {};
        const storedSessionId = localStorage.getItem('sessionId');

        console.log('[WALLET DEBUG] Generating headers. isGuest:', isGuest, 'storedSessionId:', storedSessionId);

        if (isGuest || storedSessionId) {
            if (storedSessionId) {
                headers['x-session-id'] = storedSessionId;
                headers['x-guest-mode'] = 'true';
            } else {
                console.warn('[WALLET ERROR] Guest mode but no session ID found!');
            }
        } else {
            const token = await firebaseAuth.currentUser?.getIdToken() || localStorage.getItem('token');
            if (token) headers['Authorization'] = `Bearer ${token}`;
        }
        return headers;
    };

    const fetchHistory = async () => {
        try {
            const headers = await getHeaders();
            const res = await axios.get(`${API_URL}/payments/history`, { headers });
            // Ensure we always have an array - API might return object with transactions property
            const data = res.data;
            if (Array.isArray(data)) {
                setTransactions(data);
            } else if (data && Array.isArray(data.transactions)) {
                setTransactions(data.transactions);
            } else {
                setTransactions([]);
            }
        } catch (err) {
            console.error('Failed to fetch transactions', err);
            setTransactions([]); // Ensure array on error
        }
    };

    useEffect(() => {
        fetchHistory();
        // eslint-disable-next-line
    }, []);

    // Create PayPal Order
    const createOrder = async (data, actions) => {
        // setStatus('processing'); // REMOVED: Don't block UI during popup creation
        try {
            const headers = await getHeaders();
            const res = await axios.post(`${API_URL}/payments/create-order`, {
                amount: selectedAmount,
                gateway: 'paypal'
            }, { headers });
            return res.data.id; // PayPal Order ID
        } catch (err) {
            // setStatus('error');
            console.error(err);
            throw err;
        }
    };

    // Capture PayPal Order
    const onApprove = async (data, actions) => {
        setStatus('processing'); // Set processing ONLY when user approves
        try {
            const headers = await getHeaders();
            const res = await axios.post(`${API_URL}/payments/capture-order`, {
                orderId: data.orderID,
                gateway: 'paypal'
            }, { headers });

            setStatus('success');
            if (onUpdateUser) {
                const updatedUser = res.data.user || {};
                onUpdateUser(prev => ({
                    ...prev,
                    wallet_balance: updatedUser.wallet_balance || (balance + selectedAmount)
                }));
            }
            fetchHistory();
        } catch (err) {
            setStatus('error');
            console.error(err);
        }
    };

    // Handle Cashfree UPI Payment
    const [upiStatus, setUpiStatus] = useState('idle'); // idle, processing, success, error

    const handleCashfreePayment = async () => {
        if (!cashfree) return alert("Cashfree SDK not loaded");

        setUpiStatus('processing');
        try {
            const headers = await getHeaders();

            // 1. Create Order on Backend
            const res = await axios.post(`${API_URL}/cashfree/create-order`, {
                amount: selectedINRAmount // Amount in INR
            }, { headers });

            const { payment_session_id, order_id } = res.data;

            // 2. Initiate Payment
            const checkoutOptions = {
                paymentSessionId: payment_session_id,
                redirectTarget: "_modal", // Open in modal
            };

            cashfree.checkout(checkoutOptions).then((result) => {
                if (result.error) {
                    console.error("Cashfree Error:", result.error);
                    setUpiStatus('idle'); // Reset status or handle as cancelled
                    alert("Payment cancelled or failed.");
                }
                if (result.redirect) {
                    console.log("Payment will be redirected");
                    setUpiStatus('processing'); // Keep processing while redirected
                }
                if (result.paymentDetails) {
                    console.log("Payment completed, checking status");
                    verifyCashfreePayment(order_id);
                }
            });

        } catch (err) {
            console.error("Cashfree init error:", err);
            setUpiStatus('error');
            alert("Failed to initiate payment. Please try again.");
        }
    };

    // Verify Cashfree Payment
    const verifyCashfreePayment = async (orderId) => {
        try {
            const headers = await getHeaders();
            const res = await axios.post(`${API_URL}/cashfree/verify`, {
                orderId
            }, { headers });

            if (res.data.status === 'SUCCESS') {
                setUpiStatus('success');
                setStatus('success');
                if (onUpdateUser) {
                    // Fetch latest profile to get accurate balance
                    const profileRes = await axios.get(`${API_URL}/users/me`, { headers });
                    onUpdateUser(prev => ({
                        ...prev,
                        wallet_balance: profileRes.data.wallet_balance
                    }));
                }
                fetchHistory();
            } else {
                setUpiStatus('error');
                alert(`Payment status: ${res.data.status}`);
            }
        } catch (err) {
            console.error("Verification error:", err);
            setUpiStatus('error');
        }
    };

    // OLD DOLLAR-BASED RECHARGE TIERS (for reference)
    // const RECHARGE_TIERS = { 5: 5, 20: 22, 50: 60, 100: 125, 300: 390 };

    // NEW MINUTE-BASED PRICING PLANS
    // USD Presets (for PayPal)
    const presetsUSD = [
        { label: 'Starter', amount: 5, minutes: 30, tagline: 'Perfect for quick calls', effectiveRate: '$0.167/min' },
        { label: 'Popular ⭐', amount: 15, minutes: 120, tagline: 'Best for regular family calls', effectiveRate: '$0.125/min' },
        { label: 'Best Value 🔥', amount: 30, minutes: 300, tagline: 'Great value for frequent callers', effectiveRate: '$0.10/min' }
    ];

    // INR Presets (for UPI)
    const presetsINR = [
        { label: 'Starter', amount: 99, minutes: 7, tagline: 'Quick call home', inrFormatted: '₹99', effectiveRate: '₹14/min' },
        { label: 'Popular ⭐', amount: 199, minutes: 19, tagline: 'Regular weekly calls', inrFormatted: '₹199', effectiveRate: '₹10.5/min' },
        { label: 'Value 🔥', amount: 499, minutes: 60, tagline: 'Best savings', inrFormatted: '₹499', effectiveRate: '₹8.4/min' },
        { label: 'Power', amount: 999, minutes: 143, tagline: 'Maximum minutes', inrFormatted: '₹999', effectiveRate: '₹7/min' }
    ];

    const presets = paymentMethod === 'paypal' ? presetsUSD : presetsINR;
    const currentSelected = paymentMethod === 'paypal' ? selectedAmount : selectedINRAmount;

    // Render logic
    if (status === 'success') {
        return (
            <div className="wallet-container animate-fade" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
                <SEO
                    title="Add Credits - Pay As You Go Calling"
                    description="Load credits for international calling. Secure payment via PayPal (USD) or UPI (INR). No hidden fees, pay only for what you use."
                    canonical="/wallet"
                />
                <FiCheckCircle size={64} style={{ color: 'var(--success)', marginBottom: '1.5rem' }} />
                <h2 style={{ fontSize: '1.75rem', marginBottom: '1rem' }}>Payment Successful!</h2>
                <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>
                    Your wallet has been updated. You are ready to make calls.
                </p>
                <button
                    className="primary-btn"
                    onClick={() => { setStatus('idle'); setUpiStatus('idle'); }}
                    style={{ padding: '0.75rem 2rem' }}
                >
                    Add More Funds
                </button>
            </div>
        );
    }

    return (
        <div className="wallet-container animate-fade">
            <SEO
                title="Add Credits - Pay As You Go Calling"
                description="Load credits for international calling. Secure payment via PayPal (USD) or UPI (INR). No hidden fees, pay only for what you use."
                canonical="/wallet"
            />
            {/* Header */}
            <div className="wallet-header">
                <div className="wallet-card">
                    <p style={{ opacity: 0.8, fontSize: '0.875rem', fontWeight: 500 }}>Available Minutes</p>
                    <div className="balance-amount">{Math.floor(balance)} min</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.9, fontSize: '0.875rem' }}>
                        <FiZap />
                        {isGuest ? 'Guest Session (Sign in to save)' : 'Secured Wallet'}
                    </div>
                </div>
            </div>

            <div style={{ marginTop: '2.5rem' }}>
                <h2>Add Credits</h2>

                {/* Payment Method Toggle */}
                <div style={{
                    display: 'flex',
                    gap: '0.75rem',
                    marginBottom: '1.5rem',
                    background: 'rgba(255,255,255,0.05)',
                    padding: '0.75rem',
                    borderRadius: '1rem',
                    width: 'fit-content'
                }}>
                    <button
                        onClick={() => setPaymentMethod('paypal')}
                        style={{
                            padding: '1rem 2rem',
                            borderRadius: '0.75rem',
                            border: 'none',
                            background: paymentMethod === 'paypal' ? 'var(--primary)' : 'transparent',
                            color: 'white',
                            cursor: 'pointer',
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            transition: 'all 0.2s'
                        }}
                    >
                        💳 PayPal (USD)
                    </button>
                    <button
                        onClick={() => setPaymentMethod('upi')}
                        style={{
                            padding: '1rem 2rem',
                            borderRadius: '0.75rem',
                            border: 'none',
                            background: paymentMethod === 'upi' ? 'var(--primary)' : 'transparent',
                            color: 'white',
                            cursor: 'pointer',
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            transition: 'all 0.2s'
                        }}
                    >
                        🇮🇳 UPI (INR)
                    </button>
                </div>

                <p style={{ marginBottom: '1.5rem', color: 'var(--text-muted)' }}>
                    {paymentMethod === 'paypal'
                        ? 'Select a plan and pay securely with PayPal. Get calling minutes instantly.'
                        : 'Pay in ₹ INR via UPI. Minutes added instantly.'}
                </p>

                {/* Amount Presets */}
                <div className="topup-grid" style={{
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem',
                    marginBottom: '2.5rem'
                }}>
                    {presets.map((p, i) => {
                        const isActive = currentSelected === p.amount;
                        return (
                            <button
                                key={i}
                                onClick={() => paymentMethod === 'paypal' ? setSelectedAmount(p.amount) : setSelectedINRAmount(p.amount)}
                                style={{
                                    background: isActive
                                        ? 'linear-gradient(145deg, var(--primary) 0%, #4f46e5 100%)'
                                        : 'rgba(255, 255, 255, 0.03)',
                                    border: isActive
                                        ? '1px solid rgba(255,255,255,0.2)'
                                        : '1px solid rgba(255,255,255,0.08)',
                                    padding: '1.5rem',
                                    borderRadius: '1.25rem',
                                    color: 'white',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    transform: isActive ? 'translateY(-4px)' : 'translateY(0)',
                                    boxShadow: isActive
                                        ? '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.1)'
                                        : 'none',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                            >
                                {isActive && <div style={{
                                    position: 'absolute', top: 0, left: 0, right: 0, height: '1px',
                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)'
                                }} />}

                                <span style={{
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    opacity: 0.7,
                                    marginBottom: '1rem',
                                    fontWeight: 700
                                }}>
                                    {p.label}
                                </span>

                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    marginBottom: '1rem'
                                }}>
                                    <span style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1 }}>
                                        {paymentMethod === 'paypal' ? `$${p.amount}` : p.inrFormatted}
                                    </span>
                                    <span style={{
                                        fontSize: '2rem',
                                        fontWeight: 800,
                                        lineHeight: 1,
                                        color: isActive ? 'white' : '#22c55e',
                                        textShadow: isActive ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                                    }}>
                                        {p.minutes} min
                                    </span>
                                </div>

                                <div style={{ width: '40px', height: '2px', background: 'rgba(255,255,255,0.1)', marginBottom: '1rem' }} />

                                {p.tagline ? (
                                    <span style={{
                                        fontSize: '0.85rem',
                                        opacity: 0.9,
                                        textAlign: 'center',
                                        lineHeight: '1.5',
                                        fontWeight: 500,
                                        maxWidth: '90%'
                                    }}>
                                        {p.tagline}
                                    </span>
                                ) : (
                                    <span style={{ opacity: 0 }}>Spacer</span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* 🔒 Trust Line */}
                <div style={{
                    background: 'rgba(99, 102, 241, 0.08)',
                    borderRadius: '1rem',
                    padding: '1.25rem',
                    marginBottom: '2rem',
                    border: '1px solid rgba(99, 102, 241, 0.15)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '1.25rem' }}>🔒</span>
                        <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Trust Line</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.85rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: 'var(--success)' }}>✔</span>
                            <span>No subscription</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: 'var(--success)' }}>✔</span>
                            <span>Pay only for what you use</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: 'var(--success)' }}>✔</span>
                            <span>Your family doesn't need internet or an app</span>
                        </div>
                    </div>
                </div>

                {/* Payment Area */}
                <div className="card shadow-lg" style={{
                    padding: '2.5rem',
                    textAlign: 'center',
                    background: '#ffffff',
                    color: '#0f172a',
                    borderRadius: '1.25rem',
                    border: '1px solid rgba(0,0,0,0.1)',
                    minHeight: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                }}>
                    {/* PayPal Section */}
                    {paymentMethod === 'paypal' && (
                        <>
                            {isPending && (
                                <div>
                                    <FiLoader className="spin" size={32} color="var(--primary)" />
                                    <p style={{ marginTop: '10px', color: '#64748b' }}>Loading Secure Payment...</p>
                                </div>
                            )}

                            {!isPending && status === 'idle' && (
                                <div style={{ maxWidth: '400px', margin: '0 auto', width: '100%' }}>
                                    <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center', gap: '2rem', alignItems: 'center' }}>
                                        <div style={{ textAlign: 'center' }}>
                                            <p style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700, color: '#94a3b8', margin: '0 0 4px 0' }}>You Pay</p>
                                            <span style={{ color: '#0f172a', fontSize: '1.75rem', fontWeight: 800 }}>${selectedAmount}</span>
                                        </div>
                                        <div style={{ fontSize: '1.5rem', color: '#cbd5e1' }}>→</div>
                                        <div style={{ textAlign: 'center' }}>
                                            <p style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700, color: 'var(--primary)', margin: '0 0 4px 0' }}>You Get</p>
                                            <span style={{ color: 'var(--primary)', fontSize: '1.75rem', fontWeight: 800 }}>
                                                {presetsUSD.find(p => p.amount === selectedAmount)?.minutes || 0} min
                                            </span>
                                        </div>
                                    </div>
                                    <PayPalButtons
                                        style={{ layout: "vertical", shape: "rect", color: 'gold' }}
                                        createOrder={createOrder}
                                        onApprove={onApprove}
                                    />
                                    <p style={{ marginTop: '1rem', fontSize: '0.7rem', color: '#94a3b8' }}>
                                        Secured by PayPal. Your payment data is protected.
                                    </p>
                                </div>
                            )}

                            {status === 'processing' && (
                                <div style={{ padding: '2rem' }}>
                                    <FiLoader className="spin" size={48} color="var(--primary)" />
                                    <h3 style={{ marginTop: '1rem', color: '#0f172a' }}>Verifying...</h3>
                                    <p style={{ color: '#64748b' }}>Checking payment status with PayPal servers.</p>
                                </div>
                            )}

                            {status === 'success' && (
                                <div style={{ padding: '2rem' }}>
                                    <FiCheckCircle size={56} color="var(--success)" />
                                    <h3 style={{ marginTop: '1rem', color: '#0f172a' }}>Added Successfully!</h3>
                                    <p style={{ color: '#64748b' }}>Your credits are ready to use.</p>
                                    <button className="primary" onClick={() => setStatus('idle')} style={{ marginTop: '1rem', width: '100%' }}>Ok</button>
                                </div>
                            )}

                            {status === 'error' && (
                                <div style={{ padding: '2rem' }}>
                                    <p style={{ color: 'var(--danger)', fontWeight: 600 }}>Payment verification failed.</p>
                                    <button className="primary" onClick={() => setStatus('idle')} style={{ marginTop: '1rem', width: '100%' }}>Try Again</button>
                                </div>
                            )}
                        </>
                    )}

                    {/* UPI/Cashfree Section */}
                    {paymentMethod === 'upi' && (
                        <>
                            {(upiStatus === 'idle' || upiStatus === 'error') && (
                                <div style={{ maxWidth: '400px', margin: '0 auto', width: '100%' }}>
                                    <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center', gap: '2rem', alignItems: 'center' }}>
                                        <div style={{ textAlign: 'center' }}>
                                            <p style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700, color: '#94a3b8', margin: '0 0 4px 0' }}>You Pay</p>
                                            <span style={{ color: '#0f172a', fontSize: '1.75rem', fontWeight: 800 }}>₹{selectedINRAmount.toLocaleString('en-IN')}</span>
                                        </div>
                                        <div style={{ fontSize: '1.5rem', color: '#cbd5e1' }}>→</div>
                                        <div style={{ textAlign: 'center' }}>
                                            <p style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700, color: 'var(--primary)', margin: '0 0 4px 0' }}>You Get</p>
                                            <span style={{ color: 'var(--primary)', fontSize: '1.75rem', fontWeight: 800 }}>
                                                {presetsINR.find(p => p.amount === selectedINRAmount)?.minutes || 0} min
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleCashfreePayment}
                                        style={{
                                            width: '100%',
                                            padding: '1rem 2rem',
                                            fontSize: '1.1rem',
                                            fontWeight: 700,
                                            background: 'linear-gradient(135deg, #5f259f, #8c3aaa)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '0.75rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '10px'
                                        }}
                                    >
                                        <FiSmartphone /> Pay with UPI / Card
                                    </button>
                                    <p style={{ marginTop: '1rem', fontSize: '0.7rem', color: '#94a3b8' }}>
                                        Secure payment via UPI. Support for GPay, PhonePe, Paytm, Cards & Netbanking.
                                    </p>
                                </div>
                            )}

                            {upiStatus === 'processing' && (
                                <div style={{ padding: '2rem' }}>
                                    <FiLoader className="spin" size={48} color="#5f259f" />
                                    <h3 style={{ marginTop: '1rem', color: '#0f172a' }}>Processing Payment...</h3>
                                    <p style={{ color: '#64748b' }}>Please complete the payment in the popup.</p>
                                </div>
                            )}

                            {upiStatus === 'success' && (
                                <div style={{ padding: '2rem' }}>
                                    <FiCheckCircle size={56} color="var(--success)" />
                                    <h3 style={{ marginTop: '1rem', color: '#0f172a' }}>Payment Successful!</h3>
                                    <p style={{ color: '#64748b' }}>Credits added to your wallet.</p>
                                    <button className="primary" onClick={() => setUpiStatus('idle')} style={{ marginTop: '1rem', width: '100%' }}>Ok</button>
                                </div>
                            )}

                            {upiStatus === 'error' && (
                                <div style={{ padding: '2rem' }}>
                                    <p style={{ color: 'var(--danger)', fontWeight: 600 }}>Payment failed or cancelled.</p>
                                    <button className="primary" onClick={() => { setUpiStatus('idle'); }} style={{ marginTop: '1rem', width: '100%' }}>Try Again</button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Transaction History */}
            <div style={{ marginTop: '3rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' }}>
                    <FiList size={20} color="var(--primary)" />
                    <h2 style={{ margin: 0 }}>Payment Ledger</h2>
                </div>

                {transactions.length > 0 ? (
                    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: 'rgba(255,255,255,0.05)', textAlign: 'left' }}>
                                    <th style={{ padding: '1rem' }}>Date</th>
                                    <th style={{ padding: '1rem' }}>Description</th>
                                    <th style={{ padding: '1rem' }}>Minutes</th>
                                    <th style={{ padding: '1rem' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map((tx, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                                        <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                                            {new Date(tx.createdAt).toLocaleDateString()}
                                        </td>
                                        <td style={{ padding: '1rem' }}>
                                            <div style={{ fontWeight: 600 }}>{tx.description}</div>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                                {tx.metadata?.paymentMethod === 'uropay' || tx.metadata?.paymentMethod === 'cashfree' ? '🇮🇳 UPI' : '💳 PayPal'}
                                            </div>
                                        </td>
                                        <td style={{ padding: '1rem', color: tx.amount > 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }}>
                                            {tx.amount > 0 ? '+' : ''}{Math.floor(Math.abs(tx.amount))} min
                                        </td>
                                        <td style={{ padding: '1rem' }}>
                                            <span style={{
                                                padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem',
                                                background: tx.status === 'completed' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                                color: tx.status === 'completed' ? 'var(--success)' : 'var(--danger)'
                                            }}>
                                                {tx.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                        <FiClock size={40} color="var(--text-muted)" style={{ marginBottom: '1rem' }} />
                        <p>No transactions found yet.</p>
                    </div>
                )}
            </div>

            <div className="card" style={{ marginTop: '2rem', display: 'flex', alignItems: 'center', gap: '1rem', background: 'rgba(99, 102, 241, 0.05)' }}>
                <div style={{ padding: '12px', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '12px', color: 'var(--primary)' }}>
                    <FiCreditCard size={24} />
                </div>
                <div>
                    <h3 style={{ fontSize: '1rem', marginBottom: '4px' }}>Transaction Security</h3>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                        Every payment is server-verified. We maintain a full audit trail to prevent misuse.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Wallet;
